/**
 * Welche Dateien auf ein Board dürfen.
 *
 * Diese Datei ist die einzige Wahrheit für beide Seiten. Der Server entscheidet
 * am **Inhalt** — nur der zählt, der Dateiname ist frei erfunden. Der Client
 * prüft vorab an Endung und Größe, damit niemand 400 MB durch die Leitung
 * schickt, um danach eine Absage zu lesen. Stünden die Listen zweimal
 * getrennt da, sagten beide Seiten früher oder später Verschiedenes.
 */
/** Rasterbilder: gehen unverändert ins Original und in die LOD-Pyramide. */
export const IMAGE_MIME = [
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/avif',
    'image/gif',
    'image/tiff',
];
/**
 * Vektorgrafik. Eigene Gattung, weil sie anders verarbeitet wird: Das Original
 * bleibt Vektor (und ist damit in jeder Zoomstufe scharf), die Stufen darunter
 * entstehen durch Rastern.
 */
export const VECTOR_MIME = ['image/svg+xml'];
export const VIDEO_MIME = ['video/mp4', 'video/webm', 'video/quicktime'];
/** Seitenweise zerlegt und gerastert — das PDF selbst wird kein Element. */
export const DOCUMENT_MIME = ['application/pdf'];
/** Hausschriften. OTF und TTF werden beim Hochladen nach WOFF2 gewandelt. */
export const FONT_MIME = ['font/woff2', 'font/otf', 'font/ttf'];
export const MAX_IMAGE_BYTES = 50 * 1024 * 1024;
export const MAX_VIDEO_BYTES = 500 * 1024 * 1024;
/** Eine SVG ist Text; alles darüber ist keine Grafik mehr, sondern ein Angriff. */
export const MAX_VECTOR_BYTES = 5 * 1024 * 1024;
export const MAX_PDF_BYTES = 100 * 1024 * 1024;
export const MAX_FONT_BYTES = 8 * 1024 * 1024;
/** So viele Seiten holt der Import aus einem PDF, mehr nicht. */
export const PDF_MAX_PAGES = 50;
/**
 * Endung → MIME. Nur für die Vorabprüfung im Browser: Endungen lügen, aber wer
 * `angebot.pdf` ablegt, soll nicht erst nach dem Hochladen erfahren, dass die
 * Datei zu groß ist.
 */
export const UPLOAD_EXTENSIONS = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    webp: 'image/webp',
    avif: 'image/avif',
    gif: 'image/gif',
    tif: 'image/tiff',
    tiff: 'image/tiff',
    svg: 'image/svg+xml',
    mp4: 'video/mp4',
    webm: 'video/webm',
    mov: 'video/quicktime',
    pdf: 'application/pdf',
};
/** Inhalt des `accept`-Attributs am Dateiwähler. */
export const UPLOAD_ACCEPT = [...IMAGE_MIME, ...VECTOR_MIME, ...VIDEO_MIME, ...DOCUMENT_MIME].join(',');
export const FONT_ACCEPT = '.woff2,.otf,.ttf,font/woff2,font/otf,font/ttf';
/**
 * In Meldungen: was hier überhaupt hineingeht, in Worten statt in MIME-Typen.
 * Die Formatnamen selbst bleiben unübersetzt — „JPEG" heißt überall JPEG.
 */
export const ALLOWED_SUMMARY = 'upload.allowed';
/** Obergrenze in Bytes für einen erkannten Typ. */
export function limitFor(mimeType) {
    if (VECTOR_MIME.includes(mimeType))
        return MAX_VECTOR_BYTES;
    if (VIDEO_MIME.includes(mimeType))
        return MAX_VIDEO_BYTES;
    if (DOCUMENT_MIME.includes(mimeType))
        return MAX_PDF_BYTES;
    return MAX_IMAGE_BYTES;
}
export const megabytes = (bytes) => `${Math.round(bytes / 1024 / 1024)} MB`;
/**
 * Vorabprüfung im Browser: `null` heißt „losschicken", sonst steht hier der
 * Satz, den der Nutzer zu lesen bekommt.
 *
 * Bewusst nachsichtig bei unbekannten Endungen ohne erkennbaren Typ — die
 * endgültige Entscheidung trifft der Server am Inhalt, und ein Client, der zu
 * streng ist, sperrt Dateien aus, die durchaus gingen.
 */
const ALL_MIME = new Set([
    ...IMAGE_MIME,
    ...VECTOR_MIME,
    ...VIDEO_MIME,
    ...DOCUMENT_MIME,
]);
export function uploadRejection(file, t) {
    // Nur eine echte Endung zählt: Ein Name ohne Punkt sagt gar nichts, und der
    // ganze Name als „Endung" führte zu einer Absage für etwas Erlaubtes.
    const dot = file.name.lastIndexOf('.');
    const extension = dot > 0 ? file.name.slice(dot + 1).toLowerCase() : '';
    const guessed = UPLOAD_EXTENSIONS[extension] ?? '';
    if (extension === 'heic' || extension === 'heif') {
        return t('upload.heic', { name: file.name });
    }
    if (guessed === '') {
        // Endung sagt nichts (oder fehlt): durchlassen, der Server entscheidet.
        if (extension !== '' && !(file.type !== undefined && ALL_MIME.has(file.type))) {
            return t('upload.unsupported', { name: file.name, allowed: t(ALLOWED_SUMMARY) });
        }
        return null;
    }
    const limit = limitFor(guessed);
    if (file.size > limit) {
        return t('upload.tooLarge', {
            name: file.name,
            size: megabytes(file.size),
            limit: megabytes(limit),
        });
    }
    return null;
}
//# sourceMappingURL=media.js.map